import { useState } from 'react';

import { API_BASE_URL } from '../config/config';
import {
  ApiResponse_companyInfoDTO,
  ApiResponse_iEnumerable_1,
  ApiResponse_searchResponseDTO_1,
  CompanyInfoDTO,
  SearchRequestDTO_companyInfoSearchRequestDTO,
  UserDTO,
  CompanyInfoClient,
} from '../services/apiClient';
import axiosInstance from '../services/axiosInstance';
import { handleHookError } from '../utils/hookUtils/handleHookError';

interface UseCompanyInfoApi {
  create: (data: CompanyInfoDTO) => Promise<ApiResponse_companyInfoDTO>;
  edit: (id: string, data: UserDTO) => Promise<ApiResponse_companyInfoDTO>;
  get: () => Promise<ApiResponse_iEnumerable_1>;
  getById: (id: string) => Promise<ApiResponse_companyInfoDTO>;
  search: (
    filters: SearchRequestDTO_companyInfoSearchRequestDTO,
  ) => Promise<ApiResponse_searchResponseDTO_1>;
  remove: (id: string) => Promise<void>;
  isLoading: boolean;
  error: string | null;
}

export const useCompanyInfoApi = (): UseCompanyInfoApi => {
  const apiClient = new CompanyInfoClient(API_BASE_URL, axiosInstance);

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const create = async (
    data: CompanyInfoDTO,
  ): Promise<ApiResponse_companyInfoDTO> => {
    setIsLoading(true);
    try {
      return await apiClient.createCompanyInfo(data);
    } catch (err: unknown) {
      handleHookError(err, 'Error creating Company Info', setError);
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  const edit = async (
    id: string,
    data: UserDTO,
  ): Promise<ApiResponse_companyInfoDTO> => {
    setIsLoading(true);
    try {
      return await apiClient.updateCompanyInfo(id, data);
    } catch (err: unknown) {
      handleHookError(err, 'Error updating Company Info', setError);
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  const get = async (): Promise<ApiResponse_iEnumerable_1> => {
    setIsLoading(true);
    try {
      return await apiClient.getCompanyInfos();
    } catch (err: unknown) {
      handleHookError(err, 'Error fetching Company Info', setError);
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  const getById = async (id: string): Promise<ApiResponse_companyInfoDTO> => {
    setIsLoading(true);
    try {
      return await apiClient.getCompanyInfoById(id);
    } catch (err: unknown) {
      handleHookError(err, 'Error fetching Company Info', setError);
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  const search = async (
    filters: SearchRequestDTO_companyInfoSearchRequestDTO,
  ): Promise<ApiResponse_searchResponseDTO_1> => {
    setIsLoading(true);
    try {
      return await apiClient.searchCompanyInfos(filters);
    } catch (err: unknown) {
      handleHookError(err, 'Error searching Company Info', setError);
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  const remove = async (id: string): Promise<void> => {
    setIsLoading(true);
    try {
      await apiClient.deleteCompanyInfo(id);
    } catch (err: unknown) {
      handleHookError(err, 'Error deleting Company Info', setError);
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  return { create, edit, get, getById, search, remove, isLoading, error };
};
